import { Link, type LinkProps, LinkSize, Text, TextColor, TextVariant } from '@vizit/atoms';
import { cx } from '@vizit/theme';
import type * as React from 'react';
import { Heading } from 'react-aria-components';

/** The popover's heading; it also names the popover for screen readers. */
function InfoPopoverTitle(props: { children: React.ReactNode }) {
  return (
    <Heading slot="title" level={2} className="text-md font-medium text-primary">
      {props.children}
    </Heading>
  );
}

function InfoPopoverBody(props: { children: React.ReactNode }) {
  return (
    <Text elementType="p" variant={TextVariant.SmRegular} color={TextColor.Secondary} showEllipsis={false}>
      {props.children}
    </Text>
  );
}

/** A titled group of content, such as a list of tips. */
function InfoPopoverSection(props: { title: string; children: React.ReactNode }) {
  return (
    <section className="flex flex-col gap-3">
      <Text elementType="h3" variant={TextVariant.SmSemibold} showEllipsis={false}>
        {props.title}
      </Text>
      {props.children}
    </section>
  );
}

/** A bulleted list; pass `<li>` elements as children. */
function InfoPopoverList(props: { children: React.ReactNode; className?: string }) {
  return (
    <ul className={cx('flex list-disc flex-col gap-2 pl-8 text-sm text-secondary', props.className)}>
      {props.children}
    </ul>
  );
}

/** A link that opens in a new tab and says so to screen readers. */
function InfoPopoverLink(props: Omit<LinkProps, 'target' | 'rel'>) {
  const { children, ...other } = props;

  return (
    <Link target="_blank" rel="noreferrer" size={LinkSize.Sm} {...other}>
      {(renderProps) => (
        <>
          {typeof children === 'function' ? children(renderProps) : children}
          <span className="sr-only"> (opens in a new tab)</span>
        </>
      )}
    </Link>
  );
}

export { InfoPopoverBody, InfoPopoverLink, InfoPopoverList, InfoPopoverSection, InfoPopoverTitle };
